// Default holiday list seeded from the Mediation Schedules spreadsheet.
// Categories match the spreadsheet groupings.
//
// Each holiday starts with rule kind 'unset' (no rotation configured).
// Templates and per-row edits update the rule from there.
//
// `id` is stable across sessions — used as the key in storage and in the
// rendered agreement. Don't renumber.

export const CATEGORIES = {
  holiday: 'Statutory Holidays',
  'special-day': 'Special Days',
  'school-break': 'School Breaks',
  christmas: 'Christmas & New Year',
}

// `longWeekendType` indicates whether the holiday falls adjacent to a weekend:
//   'always-monday'  — falls on a Monday every year (long weekend = Sat–Mon)
//   'always-friday'  — falls on a Friday every year (long weekend = Fri–Sun)
//   'sometimes'      — fixed-date holiday that may or may not land on Mon/Fri
//   null/undefined   — not a long-weekend candidate

// Statutory & commonly-observed holidays per province.
const STAT_HOLIDAYS_BC = [
  { id: 'family-day',           category: 'holiday', name: 'Family Day',                    longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                   longWeekendType: 'always-friday' },
  { id: 'easter-monday',        category: 'holiday', name: 'Easter Monday',                 longWeekendType: 'always-monday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                  longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                    longWeekendType: 'sometimes' },
  { id: 'bc-day',               category: 'holiday', name: 'BC Day',                        longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                    longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'Day of Truth & Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Monday',           longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',               longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_AB = [
  { id: 'family-day',           category: 'holiday', name: 'Family Day',                                longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'heritage-day',         category: 'holiday', name: 'Heritage Day',                              longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_MB = [
  { id: 'louis-riel-day',       category: 'holiday', name: 'Louis Riel Day',                            longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'terry-fox-day',        category: 'holiday', name: 'Terry Fox Day',                             longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_NB = [
  { id: 'family-day',           category: 'holiday', name: 'Family Day',                                longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'new-brunswick-day',    category: 'holiday', name: 'New Brunswick Day',                         longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_NL = [
  { id: 'st-patricks-day',      category: 'holiday', name: "St. Patrick's Day",                         longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'st-georges-day',       category: 'holiday', name: "St. George's Day",                          longWeekendType: 'always-monday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'discovery-day-nl',     category: 'holiday', name: 'Discovery Day',                             longWeekendType: 'always-monday' },
  { id: 'memorial-day',         category: 'holiday', name: 'Memorial Day',                              longWeekendType: 'sometimes' },
  { id: 'orangemans-day',       category: 'holiday', name: "Orangeman's Day",                           longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_NS = [
  { id: 'heritage-day-ns',      category: 'holiday', name: 'Heritage Day',                              longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'easter-monday',        category: 'holiday', name: 'Easter Monday',                             longWeekendType: 'always-monday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'civic-holiday',        category: 'holiday', name: 'August Civic Holiday',                      longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_NT = [
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'easter-monday',        category: 'holiday', name: 'Easter Monday',                             longWeekendType: 'always-monday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'national-indigenous-peoples-day', category: 'holiday', name: 'National Indigenous Peoples Day', longWeekendType: 'sometimes' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'civic-holiday',        category: 'holiday', name: 'August Civic Holiday',                      longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_NU = [
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'nunavut-day',          category: 'holiday', name: 'Nunavut Day',                               longWeekendType: 'sometimes' },
  { id: 'civic-holiday',        category: 'holiday', name: 'August Civic Holiday',                      longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_ON = [
  { id: 'family-day',           category: 'holiday', name: 'Family Day',                                longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'civic-holiday',        category: 'holiday', name: 'August Civic Holiday',                      longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_PE = [
  { id: 'islander-day',         category: 'holiday', name: 'Islander Day',                              longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'easter-monday',        category: 'holiday', name: 'Easter Monday',                             longWeekendType: 'always-monday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'gold-cup-parade-day',  category: 'holiday', name: 'Gold Cup Parade Day',                       longWeekendType: 'always-friday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_SK = [
  { id: 'family-day',           category: 'holiday', name: 'Family Day',                                longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'easter-monday',        category: 'holiday', name: 'Easter Monday',                             longWeekendType: 'always-monday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'saskatchewan-day',     category: 'holiday', name: 'Saskatchewan Day',                          longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

const STAT_HOLIDAYS_YT = [
  { id: 'family-day',           category: 'holiday', name: 'Family Day',                                longWeekendType: 'always-monday' },
  { id: 'good-friday',          category: 'holiday', name: 'Good Friday',                               longWeekendType: 'always-friday' },
  { id: 'easter-monday',        category: 'holiday', name: 'Easter Monday',                             longWeekendType: 'always-monday' },
  { id: 'victoria-day',         category: 'holiday', name: 'Victoria Day',                              longWeekendType: 'always-monday' },
  { id: 'national-indigenous-peoples-day', category: 'holiday', name: 'National Indigenous Peoples Day', longWeekendType: 'sometimes' },
  { id: 'canada-day',           category: 'holiday', name: 'Canada Day',                                longWeekendType: 'sometimes' },
  { id: 'discovery-day',        category: 'holiday', name: 'Discovery Day',                             longWeekendType: 'always-monday' },
  { id: 'labour-day',           category: 'holiday', name: 'Labour Day',                                longWeekendType: 'always-monday' },
  { id: 'truth-reconciliation', category: 'holiday', name: 'National Day for Truth and Reconciliation', longWeekendType: 'sometimes' },
  { id: 'thanksgiving',         category: 'holiday', name: 'Thanksgiving Day',                          longWeekendType: 'always-monday' },
  { id: 'remembrance-day',      category: 'holiday', name: 'Remembrance Day',                           longWeekendType: 'sometimes' },
]

// Map of jurisdiction code → the province's statutory & commonly-observed
// holiday list. Unknown codes fall back to BC in buildInitialHolidays.
const STAT_HOLIDAYS_BY_JURISDICTION = {
  BC: STAT_HOLIDAYS_BC,
  AB: STAT_HOLIDAYS_AB,
  MB: STAT_HOLIDAYS_MB,
  NB: STAT_HOLIDAYS_NB,
  NL: STAT_HOLIDAYS_NL,
  NS: STAT_HOLIDAYS_NS,
  NT: STAT_HOLIDAYS_NT,
  NU: STAT_HOLIDAYS_NU,
  ON: STAT_HOLIDAYS_ON,
  PE: STAT_HOLIDAYS_PE,
  SK: STAT_HOLIDAYS_SK,
  YT: STAT_HOLIDAYS_YT,
}

// Everything below is shared across provinces. Special days, school breaks,
// and the Christmas/New Year window aren't statutory and don't vary.
const SHARED_HOLIDAYS = [
  // Special days
  { id: 'pro-d-days',         category: 'special-day',  name: 'Professional Development Days' },
  { id: 'mothers-day',        category: 'special-day',  name: "Mother's Day" },
  { id: 'fathers-day',        category: 'special-day',  name: "Father's Day" },
  { id: 'childrens-birthday', category: 'special-day',  name: "Children's Birthday" },
  { id: 'party-a-birthday',   category: 'special-day',  name: "{{partyA}}'s Birthday" },
  { id: 'party-b-birthday',   category: 'special-day',  name: "{{partyB}}'s Birthday" },
  { id: 'halloween',          category: 'special-day',  name: 'Halloween' },

  // School breaks
  { id: 'spring-break',       category: 'school-break', name: 'Spring Break' },
  { id: 'summer-break',       category: 'school-break', name: 'Summer Break' },
  { id: 'winter-break',       category: 'school-break', name: 'Winter Break' },

  // Christmas
  { id: 'christmas-eve',      category: 'christmas',    name: 'Christmas Eve' },
  { id: 'christmas-day',      category: 'christmas',    name: 'Christmas Day' },
  { id: 'new-years-eve',      category: 'christmas',    name: "New Year's Eve" },
  { id: 'new-years-day',      category: 'christmas',    name: "New Year's Day" },
]

// Union of every possible holiday across all jurisdictions. Used by
// store.js's migration to look up a holiday's longWeekendType by id
// regardless of which province seeded the draft. Order isn't significant here.
export const SEED_HOLIDAYS = (function () {
  var seen = {}
  var all = []
  var codes = Object.keys(STAT_HOLIDAYS_BY_JURISDICTION)
  for (var i = 0; i < codes.length; i++) {
    var list = STAT_HOLIDAYS_BY_JURISDICTION[codes[i]]
    for (var j = 0; j < list.length; j++) {
      if (!seen[list[j].id]) { seen[list[j].id] = true; all.push(list[j]) }
    }
  }
  for (var k = 0; k < SHARED_HOLIDAYS.length; k++) {
    if (!seen[SHARED_HOLIDAYS[k].id]) { seen[SHARED_HOLIDAYS[k].id] = true; all.push(SHARED_HOLIDAYS[k]) }
  }
  return all
})()

// Rule kinds form two tiers:
//   Top-tier "approach":
//     'unset'        — no choice yet (status: Needs decision)
//     'no-change'    — keep the base parenting schedule on this day
//     'suspend-flex' — suspend the base schedule; agree per occurrence
//   Bottom tier (only when approach is "set a specific arrangement"):
//     'fixed', 'alternating-by-year', 'split-day', 'share'
//
// The UI presents a 3-option approach picker first; choosing
// "Set a specific arrangement" then reveals the rotation picker.
export const DEFAULT_RULE = { kind: 'unset' }

// Used by the UI to know which rule.kind values count as "specific arrangement"
export const SPECIFIC_RULE_KINDS = [
  'fixed',
  'alternating-by-year',
  'split-day',         // single-day split at a transition time
  'split-period',      // multi-day break split into first/second half (school breaks)
  'share',
  'week-on-off',       // 1-week-on / 1-week-off rotation (school breaks)
  'paired-day-split',  // 24-hour Eve/Day block split at a chosen time, alternating yearly
                        // (Christmas Eve/Day, New Year's Eve/Day)
]

// Lookup: which holidays support the paired-day-split rule (Eve/Day pairs).
// Each entry maps holiday.id → date labels used in the generated prose.
export const PAIRED_DAY_RANGES = {
  'christmas-eve':  { from: 'December 24th', to: 'December 25th' },
  'christmas-day':  { from: 'December 25th', to: 'December 26th' },
  'new-years-eve':  { from: 'December 31st', to: 'January 1st' },
  'new-years-day':  { from: 'January 1st',   to: 'January 2nd' },
}

// Builds a fresh holiday state with empty rules for the given jurisdiction.
// Falls back to BC if the jurisdiction is unknown.
export function buildInitialHolidays(partyAName, partyBName, jurisdiction) {
  var stat = STAT_HOLIDAYS_BY_JURISDICTION[jurisdiction || 'BC'] || STAT_HOLIDAYS_BC
  var list = stat.concat(SHARED_HOLIDAYS)
  return list.map(function (h) {
    return {
      id: h.id,
      category: h.category,
      name: h.name
        .replace('{{partyA}}', partyAName || 'Party A')
        .replace('{{partyB}}', partyBName || 'Party B'),
      longWeekendType: h.longWeekendType || null,
      rule: Object.assign({}, DEFAULT_RULE),
      pickup: '',
      dropoff: '',
      timesByAgreement: false,
      customProse: '',
      notes: '',
      whyThisMatters: '',
      flaggedForDiscussion: false,
      // Secondary-role discussion note. Only Party B (role: 'secondary')
      // writes here; Party A sees it read-only alongside a "Mark resolved"
      // action that clears both the note and the flag together. Null when
      // empty; when set, shape is { text, authorName, updatedAt } so the
      // primary view can display "Note from {name}" instead of a generic
      // "the other party" label.
      discussionNote: null,
      // True once the user has manually renamed this row's display name.
      // Prevents refreshPlaceholderNames() from overwriting user renames
      // on the three placeholder rows (party-a-birthday, party-b-birthday,
      // childrens-birthday) when party names or children change.
      customName: false,
    }
  })
}
